import { UserVariables, UpdateUserInput, UserType } from "types";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

export class ProfileHandling {
  private static client = generateClient<Schema>({
    authMode: "userPool",
  });

  private static async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userData = await this.getUserDataFromToken(idToken);
    return { idToken, token, userData };
  }

  static async fetchUser(id: string) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.models.User.get(
        {
          id,
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        throw new Error(`GraphQL errors: ${response.errors.map((err: any) => err.message).join(", ")}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async updateUser(input: UpdateUserInput) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.models.User.update(input, {
        authMode: "userPool",
        authToken: token,
      });
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        throw new Error(`GraphQL errors: ${response.errors.map((err: any) => err.message).join(", ")}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getUserDataFromToken(idToken: any): Promise<UserVariables> {
    const email = String(idToken.payload.email);
    const id = `${idToken.payload.sub}::${idToken.payload.sub}`;
    const userType = String(idToken.payload["custom:userType"]);
    const domain = String(idToken.payload["custom:domain"]);

    const userData: UserVariables = {
      id: id,
      userDisplayName: email.split("@")[0],
      userType: UserType[userType.toUpperCase() as keyof typeof UserType],
      personalInformation: {
        email: email.toLowerCase(),
      },
    };

    if (domain) {
      userData.domain = String(domain).toLowerCase();
    }

    return userData;
  }
}
